'use client';

import { useState, useEffect } from 'react';
import { useSyncExternalStore } from 'react';
import { useThreadRuntime } from '@assistant-ui/react';
import { User, Mic2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SpeakerContextIndicatorProps {
  className?: string;
}

interface DetectedSpeaker {
  name: string;
  hasProfile: boolean;
  hasToneProfile: boolean;
}

export function SpeakerContextIndicator({ className }: SpeakerContextIndicatorProps) {
  const threadRuntime = useThreadRuntime();
  const [activeSpeaker, setActiveSpeaker] = useState<DetectedSpeaker | null>(null);

  const threadState = useSyncExternalStore(
    threadRuntime.subscribe,
    () => threadRuntime.getState(),
    () => threadRuntime.getState()
  );

  useEffect(() => {
    const messages = threadState.messages;

    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role !== 'assistant') continue;

      const content = msg.content || [];

      for (const part of content) {
        // Check for tool-result type (cast to any for compatibility with assistant-ui types)
        if ((part as any).type === 'tool-result') {
          const result = (part as any).result;

          if (result?.speaker || result?.speakerName) {
            const speakerName = result.speaker || result.speakerName;
            const hasProfile = result.hasProfile || result.profileId || result.fromProfile;
            const hasToneProfile = result.toneAvailable || result.toneProfile || result.tone_profile;

            setActiveSpeaker({
              name: speakerName,
              hasProfile: !!hasProfile,
              hasToneProfile: !!hasToneProfile,
            });
            return;
          }

          if (result?.speakers && Array.isArray(result.speakers)) {
            const labeledSpeaker = result.speakers.find((s: any) => s.name && s.name !== s.label);
            if (labeledSpeaker) {
              setActiveSpeaker({
                name: labeledSpeaker.name,
                hasProfile: !!labeledSpeaker.hasProfile || !!labeledSpeaker.profileId,
                hasToneProfile: !!labeledSpeaker.toneAvailable,
              });
              return;
            }
          }
        }
      }
    }

    setActiveSpeaker(null);
  }, [threadState.messages]);

  if (!activeSpeaker) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex items-center gap-1.5 px-2 py-1 rounded-full text-xs",
        "bg-purple-50 dark:bg-purple-950/30 text-purple-700 dark:text-purple-300",
        "border border-purple-200 dark:border-purple-800",
        className
      )}
    >
      {activeSpeaker.hasToneProfile ? (
        <Mic2 className="h-3 w-3" />
      ) : (
        <User className="h-3 w-3" />
      )}
      <span className="font-medium">{activeSpeaker.name}</span>
      {activeSpeaker.hasToneProfile && (
        <span className="text-purple-500 dark:text-purple-400">(voice ready)</span>
      )}
    </div>
  );
}
